import React, { useEffect, useState, useCallback, useRef } from "react";
import { Form, Button, Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { 
  FiSearch, FiHash, FiShoppingCart, FiMapPin, FiStar, FiEdit3, FiX, 
  FiChevronDown, FiChevronUp, FiCheck, FiPlus, FiTrash2, FiSettings, 
  FiCheckSquare, FiSquare, FiPrinter 
} from "react-icons/fi";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "../styles/style.scss";
import "../styles/ShoppingList.scss";
import { animations, transitions, layoutAnimation } from "./ShoppingList/animationUtils";
import LoadingSkeleton from "./ShoppingList/LoadingSkeleton";

function ShoppingList() {
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [aisles, setAisles] = useState([]);
  const [selectedAisle, setSelectedAisle] = useState({});
  const [selectedIngredient, setSelectedIngredient] = useState({});
  const [selectedIngredientUnit, setSelectedIngredientUnit] = useState("");
  const [ingredientQty, setIngredientQty] = useState(1);
  const [isMetric, setIsMetric] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [successAnimation, setSuccessAnimation] = useState(null);
  const [printMode, setPrintMode] = useState(false);
  const componentRef = useRef();

  // Favorited
  const [favIngredientOptions, setFavIngredientOptions] = useState([]);
  const [favIngredientQty, setFavIngredientQty] = useState(1);
  const [favSelectedIngredient, setFavSelectedIngredient] = useState({});
  const [favSelectedIngredientUnit, setFavSelectedIngredientUnit] =
    useState("");

  // Add/Remove Modal Variables
  const [modalSelectedIngredient, setModalSelectedIngredient] = useState({});

  // Edit Favorites
  const [editFav, setEditFav] = useState(null);
  const [showFavoritesPanel, setShowFavoritesPanel] = useState(false);
  
  // Multi-select mode
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Day filter state
  const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Unspecified'];
  const [selectedDays, setSelectedDays] = useState(ALL_DAYS);
  const [dayFilterLoaded, setDayFilterLoaded] = useState(false);
  const saveFilterTimeoutRef = useRef(null);

  const fetchUserMetricSetting = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/getUser`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { user } = await response.json();

      if (!user) {
        // Handle error
      }

      setIsMetric(user.default_unit_of_measurement === "metric");
    } catch (error) {
      // Handle error
    }
  };

  const handleEditMode = () => {
    setEditMode(!editMode);
  };
  
  const handleMultiSelectMode = () => {
    setMultiSelectMode(!multiSelectMode);
    setSelectedItems(new Set()); // Clear selections when toggling mode
  };
  
  const toggleItemSelection = (itemIds) => {
    // itemIds can be a single ID or an array of IDs (for aggregated items)
    const ids = Array.isArray(itemIds) ? itemIds : [itemIds];
    const newSelected = new Set(selectedItems);

    // Check if all IDs are already selected
    const allSelected = ids.every(id => newSelected.has(id));

    if (allSelected) {
      // Deselect all
      ids.forEach(id => newSelected.delete(id));
    } else {
      // Select all
      ids.forEach(id => newSelected.add(id));
    }
    setSelectedItems(newSelected);
  };
  
  const selectAllItems = () => {
    // Select all items that pass the current day filter (before aggregation)
    const filteredItems = shoppingListItems.filter((item) => {
      // Check if day is "unspecified" (null, undefined, empty string, or "None")
      const isUnspecified = item.day === null || item.day === undefined || item.day === "" || item.day === "None";
      if (isUnspecified) {
        return selectedDays.includes("Unspecified");
      }
      return selectedDays.includes(item.day);
    });
    const allItemIds = new Set(filteredItems.map(item => item.id));
    setSelectedItems(allItemIds);
  };
  
  const clearSelection = () => {
    setSelectedItems(new Set());
  };
  
  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;

    const confirmed = window.confirm(`Are you sure you want to delete ${selectedItems.size} selected items?`);
    if (!confirmed) return;

    // Process each selected item
    const itemsToDelete = shoppingListItems.filter(item => selectedItems.has(item.id));

    Promise.all(itemsToDelete.map(item => {
      const requestBody = {
        aisle_id: item.aisle_id ?? null,
        aisle_name: item.aisle_name ?? null,
        day: item.day ?? null,
        for_recipe: item.for_recipe ?? null,
        item_name: item.item_name ?? null,
        meal: item.meal ?? null,
        original_id_string: item.original_id_string ?? null,
        qty: item.qty ?? null,
        sort_order: item.sort_order ?? null,
        unit_name: item.unit_name ?? null,
        selectedDays: selectedDays, // Pass selected days for day-aware deletion
      };

      return fetch(`${process.env.REACT_APP_API_URL}/move-item`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
    }))
    .then(() => {
      fetchShoppingList();
      fetchDeletedShoppingList();
      setSelectedItems(new Set());
      setMultiSelectMode(false);
    })
    .catch(err => {
      console.error("Error deleting items:", err);
    });
  };

  const handleFavEditMode = () => {
    setShowFavoritesPanel(!showFavoritesPanel);
  };

  const handleToggle = () => {
    setIsMetric(!isMetric);

    // Make a POST request to update user's default_unit_of_measurement value
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        default_unit_of_measurement: isMetric ? "traditional" : "metric",
      }),
    }).then((response) => {
      fetchShoppingList();
      if (!response.ok) {
        // Handle error
      }
    });
  };

  const [isLoggedIn, setIsLoggedIn] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/isLoggedIn`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
        } else {
          throw new Error("User is not logged in");
        }
      })
      .catch((error) => {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      });
  }, []);
  const [itemQty, setItemQty] = useState(1);
  const [itemName, setItemName] = useState("");

  const [shoppingListItems, setShoppingListItems] = useState([]);
  const [deletedShoppingListItems, setDeletedShoppingListItems] = useState([]);
  const [collapsedAisles, setCollapsedAisles] = useState({});

  const [selectedItemEditAisle, setSelectedItemEditAisle] = useState(null);
  const [selectedItemEditItemName, setSelectedItemEditItemName] = useState("");

  const [selectedEditAisle, setSelectedEditAisle] = useState({});

  // ingredient Qty:
  const handleIngredientQtyChange = (ingredientQtyValue) => {
    if (!isNaN(ingredientQtyValue)) {
      setIngredientQty(ingredientQtyValue);
    } else {
      setIngredientQty("");
    }
  };

  // fav ingredient Qty:
  const handleFavIngredientQtyChange = (favIngredientQtyValue) => {
    if (!isNaN(favIngredientQtyValue)) {
      setFavIngredientQty(favIngredientQtyValue);
    } else {
      setFavIngredientQty("");
    }
  };

  //Handle Form Changes
  const handleItemQtyChange = (itemQtyValue) => {
    if (!isNaN(itemQtyValue)) {
      setItemQty(itemQtyValue);
    } else {
      setItemQty("");
    }
  };

  //Handle Form Changes
  const handleItemNameChange = (event) => {
    setItemName(event.target.value);
  };

  //Edit AIsle
  const handleEditInstruction = (item) => {
    setSelectedItemEditAisle(item);
    setSelectedItemEditItemName(item.item_name);
    setSelectedEditAisle(item);
    console.log(item);
  };

  const fetchIngredients = () => {
    return fetch(
      `${process.env.REACT_APP_API_URL}/ingredients_and_non_cooking_items`,
      {
        credentials: "include",
      }
    )
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setIngredientOptions(data);
      })
      .catch((err) => {
        console.error("Error retrieving user ingredients:", err);
      });
  };

  const fetchFavIngredients = () => {
    return fetch(
      `${process.env.REACT_APP_API_URL}/fav_ingredients_and_non_cooking_items`,
      {
        credentials: "include",
      }
    )
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setFavIngredientOptions(data);
      })
      .catch((err) => {
        console.error("Error retrieving user ingredients:", err);
      });
  };

  const fetchAisles = () => {
    return fetch(`${process.env.REACT_APP_API_URL}/api/shopping_aisle`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setAisles(data);
      })
      .catch((err) => {
        console.error("Error retrieving user units:", err);
      });
  };

  const fetchShoppingList = () => {
    return fetch(`${process.env.REACT_APP_API_URL}/api/shopping_list2`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("---");
        console.log(data); // Check if data is loaded correctly
        console.log("---");
        setShoppingListItems(data);
      })
      .catch((err) => {
        console.error("Error retrieving user units:", err);
      });
  };

  const fetchDeletedShoppingList = () => {
    return fetch(`${process.env.REACT_APP_API_URL}/api/deleted_shopping_list`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data); // Check if data is loaded correctly
        setDeletedShoppingListItems(data);
      })
      .catch((err) => {
        console.error("Error retrieving user units:", err);
      });
  };

  const handleAddIngredient = () => {
    // Show success animation
    setSuccessAnimation('ingredient');
    // Check if an ingredient is selected
    if (!selectedIngredient.id) {
      alert("Please select an ingredient");
      return;
    }
    
    // Split ingredientId to get the ID
    const ingredient_id = selectedIngredient.id.split(",")[0];
    // Determine if ingredient is custom
    const ingredient_true = selectedIngredient.id.includes("non-cooking")
      ? null
      : 1;
    const customIngredientFlag = selectedIngredient.id.includes("user") ? 1 : 0;
    const nonIngredientFlag = selectedIngredient.id.includes("non-cooking")
      ? 1
      : null;

    // Send POST request to add new unit
    fetch(`${process.env.REACT_APP_API_URL}/add-shopping-item`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ingredientId: ingredient_id,
        ingredient_true: ingredient_true,
        custom_ingredient: customIngredientFlag,
        nonIngredientFlag: nonIngredientFlag,
        qty: ingredientQty * selectedIngredient.conversion_factor,
        original_id: selectedIngredient.id,
      }),
    })
      .then((res) => {
        fetchShoppingList();
        setItemQty(1);
        setItemName("");
        setSelectedAisle({});
        setIngredientQty(1);
        setSelectedIngredient({});
        setSelectedIngredientUnit("");
        // Clear success animation after delay
        setTimeout(() => setSuccessAnimation(null), 1000);
        if (res.ok) {
          // Reload units data
        } else {
          throw new Error("Error adding new unit");
        }
      })
      .catch((err) => {
        console.error("Error adding new unit:", err);
      });
  };

  const handleFavAddIngredient = () => {
    // Show success animation
    setSuccessAnimation('favorite');
    // Check if an ingredient is selected
    if (!favSelectedIngredient.id) {
      alert("Please select a favorite ingredient");
      return;
    }
    
    // Split ingredientId to get the ID
    const ingredient_id = favSelectedIngredient.id.split(",")[0];
    // Determine if ingredient is custom
    const ingredient_true = favSelectedIngredient.id.includes("non-cooking")
      ? null
      : 1;
    const customIngredientFlag = favSelectedIngredient.id.includes("user")
      ? 1
      : 0;
    const nonIngredientFlag = favSelectedIngredient.id.includes("non-cooking")
      ? 1
      : null;

    // Send POST request to add new unit
    fetch(`${process.env.REACT_APP_API_URL}/add-shopping-item`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ingredientId: ingredient_id,
        ingredient_true: ingredient_true,
        custom_ingredient: customIngredientFlag,
        nonIngredientFlag: nonIngredientFlag,
        qty: favIngredientQty * favSelectedIngredient.conversion_factor,
        original_id: favSelectedIngredient.id,
      }),
    })
      .then((res) => {
        fetchShoppingList();
        setItemQty(1);
        setItemName("");
        setSelectedAisle({});
        setIngredientQty(1);
        setSelectedIngredient({});
        setSelectedIngredientUnit("");
        setFavIngredientQty(1);
        setFavSelectedIngredient({});
        setFavSelectedIngredientUnit("");
        // Clear success animation after delay
        setTimeout(() => setSuccessAnimation(null), 1000);
        if (res.ok) {
          // Reload units data
        } else {
          throw new Error("Error adding new unit");
        }
      })
      .catch((err) => {
        console.error("Error adding new unit:", err);
      });
  };

  const handleAddManualItem = () => {
    // Show success animation
    setSuccessAnimation('manual');
    // Validate inputs
    if (!itemName.trim()) {
      alert("Please enter an item name");
      return;
    }
    if (!selectedAisle.id) {
      alert("Please select an aisle");
      return;
    }
    
    // Send POST request to add new unit
    fetch(`${process.env.REACT_APP_API_URL}/add-manual-shopping-item`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        manual_name: itemName,
        manual_aisle_id: selectedAisle.id,
        manual_qty: itemQty,
      }),
    })
      .then((res) => {
        fetchShoppingList();
        setItemQty(1);
        setItemName("");
        setSelectedAisle({});
        setIngredientQty(1);
        setSelectedIngredient({});
        setSelectedIngredientUnit("");
        // Clear success animation after delay
        setTimeout(() => setSuccessAnimation(null), 1000);
        if (res.ok) {
          // Reload units data
        } else {
          throw new Error("Error adding new unit");
        }
      })
      .catch((err) => {
        console.error("Error adding new unit:", err);
      });
  };

  const handleDeleteItem = (item) => {
    const requestBody = {
      aisle_id: item.aisle_id ?? null,
      aisle_name: item.aisle_name ?? null,
      day: item.day ?? null,
      for_recipe: item.for_recipe ?? null,
      item_name: item.item_name ?? null,
      meal: item.meal ?? null,
      original_id_string: item.original_id_string ?? null,
      qty: item.qty ?? null,
      sort_order: item.sort_order ?? null,
      unit_name: item.unit_name ?? null,
      selectedDays: selectedDays, // Pass selected days for day-aware deletion
    };

    fetch(`${process.env.REACT_APP_API_URL}/move-item`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then((res) => {
        if (res.ok) {
          console.log("success");
          fetchShoppingList();
          fetchDeletedShoppingList();
        } else {
          throw new Error("Error moving aisle");
        }
      })
      .catch((err) => {
        console.error("Error deleting aisle:", err);
      });
  };

  //remove item from favorites
  const handleRemoveFavorite = async (item) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove "${item.ingredient_name}" from your favorites list?`
    );
    if (!confirmed) return;

    const isUserIngredient = item.id.includes("user") ? 1 : 0;
    const isNonCooking = item.id.includes("non") ? 1 : 0;

    fetch(`${process.env.REACT_APP_API_URL}/shopping_remove_favorite`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        itemId: item.id,
        isUserIngredient: isUserIngredient,
        isNonCooking: isNonCooking,
      }),
    })
      .then((res) => {
        if (res.ok) {
          console.log("success");
          fetchFavIngredients();
        } else {
          throw new Error("Error moving aisle");
        }
      })
      .catch((err) => {
        console.error("Error deleting aisle:", err);
      });
  };

  //Add item to a User's FavoritesList
  const handleAddFavoriteToList = async () => {
    const isUserIngredient = modalSelectedIngredient.id.includes("user")
      ? 1
      : 0;
    const isNonCooking = modalSelectedIngredient.id.includes("non") ? 1 : 0;

    fetch(`${process.env.REACT_APP_API_URL}/shopping_add_favorite`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        itemId: modalSelectedIngredient.id,
        isUserIngredient: isUserIngredient,
        isNonCooking: isNonCooking,
      }),
    })
      .then((res) => {
        if (res.ok) {
          console.log("success");
          fetchFavIngredients();
        } else {
          throw new Error("Error moving aisle");
        }
      })
      .catch((err) => {
        console.error("Error deleting aisle:", err);
      });
  };


  const handlePrint = useCallback(() => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setPrintMode(false);
    }, 100);
  }, []);

  const handleClearDeletedItems = (item) => {
    fetch(`${process.env.REACT_APP_API_URL}/api/clear-crossed-off-items`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.ok) {
          console.log("success");
          fetchDeletedShoppingList();
        } else {
          throw new Error("Error deleting");
        }
      })
      .catch((err) => {
        console.error("Error deleting:", err);
      });
  };

  const handleClearAllItems = () => {
    const confirmed = window.confirm("Are you sure you want to clear all items from your shopping list? This action cannot be undone.");
    if (!confirmed) return;

    // Process all items in the shopping list
    Promise.all(shoppingListItems.map(item => {
      const requestBody = {
        aisle_id: item.aisle_id ?? null,
        aisle_name: item.aisle_name ?? null,
        day: item.day ?? null,
        for_recipe: item.for_recipe ?? null,
        item_name: item.item_name ?? null,
        meal: item.meal ?? null,
        original_id_string: item.original_id_string ?? null,
        qty: item.qty ?? null,
        sort_order: item.sort_order ?? null,
        unit_name: item.unit_name ?? null,
      };
      
      return fetch(`${process.env.REACT_APP_API_URL}/move-item`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
    }))
    .then(() => {
      fetchShoppingList();
      fetchDeletedShoppingList();
    })
    .catch(err => {
      console.error("Error clearing all items:", err);
      alert("Failed to clear some items. Please try again.");
    });
  };

  // Group items by aisle
  const groupItemsByAisle = (items) => {
    const grouped = items.reduce((acc, item) => {
      // Handle null, undefined, empty string, or whitespace-only aisle names
      const aisleName = (item.aisle_name && item.aisle_name.trim()) ? item.aisle_name : "No Aisle";
      if (!acc[aisleName]) {
        acc[aisleName] = {
          items: [],
          sortOrder: item.sort_order !== null && item.sort_order !== undefined ? item.sort_order : 999
        };
      }
      acc[aisleName].items.push(item);
      return acc;
    }, {});
    
    // Sort aisles with "No Aisle" first, then by sort_order from database
    const sortedAisles = Object.entries(grouped).sort(([aNameA, aDataA], [aNameB, aDataB]) => {
      // Ensure "No Aisle" always comes first
      if (aNameA === "No Aisle") return -1;
      if (aNameB === "No Aisle") return 1;
      // Sort the rest by sort_order
      return aDataA.sortOrder - aDataB.sortOrder;
    });
    
    // Return array of [aisleName, items] pairs in sorted order
    return sortedAisles.map(([aisleName, aisleData]) => [aisleName, aisleData.items]);
  };

  // Toggle aisle collapse state
  const toggleAisleCollapse = (aisleName) => {
    setCollapsedAisles(prev => ({
      ...prev,
      [aisleName]: !prev[aisleName]
    }));
  };

  // Render shopping list items grouped by aisle with animations
  const renderShoppingListByAisle = () => {
    const filteredItems = getFilteredItems(shoppingListItems);
    const groupedItems = groupItemsByAisle(filteredItems);
    console.log("Grouped items order:", groupedItems.map(([aisle]) => aisle));
    
    return (
      <LayoutGroup>
        <AnimatePresence mode="popLayout">
          {groupedItems.map(([aisleName, items]) => (
            <motion.div 
              key={aisleName} 
              className={`aisle-group ${aisleName === "No Aisle" ? "no-aisle-group" : ""}`}
              layout
              initial={animations.card.initial}
              animate={animations.card.animate}
              exit={animations.fade.exit}
              whileHover={animations.card.hover}
              transition={transitions.smooth}
            >
              <motion.div 
                className="aisle-header"
                onClick={() => toggleAisleCollapse(aisleName)}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="aisle-name">
                  <FiMapPin className="aisle-icon" />
                  {aisleName}
                </h4>
                <div className="aisle-info">
                  <span className="aisle-count">{items.length} items</span>
                  <motion.div
                    animate={{ rotate: collapsedAisles[aisleName] ? 0 : 180 }}
                    transition={{ duration: 0.2 }}
                  >
                    {collapsedAisles[aisleName] ? <FiChevronDown /> : <FiChevronUp />}
                  </motion.div>
                </div>
              </motion.div>
              
              <AnimatePresence>
                {!collapsedAisles[aisleName] && (
                  <motion.div 
                    className="aisle-items"
                    initial={animations.collapse.initial}
                    animate={animations.collapse.animate}
                    exit={animations.collapse.exit}
                    transition={animations.collapse.transition}
                  >
                    <AnimatePresence mode="popLayout">
                      {items.map((item) => (
                        <motion.div
                          key={item._allIds ? item._allIds.join('-') : item.id}
                          className={`shopping-item-card ${multiSelectMode && (item._allIds || [item.id]).some(id => selectedItems.has(id)) ? 'selected' : ''}`}
                          layout
                          initial={animations.listItem.initial}
                          animate={animations.listItem.animate}
                          exit={animations.listItem.exit}
                          transition={animations.listItem.transition}
                          whileHover={{ x: 5 }}
                        >
                          <div className="item-main">
                            {multiSelectMode ? (
                              <motion.button
                                type="button"
                                className="select-checkbox"
                                onClick={() => toggleItemSelection(item._allIds || [item.id])}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                {(item._allIds || [item.id]).every(id => selectedItems.has(id)) ? <FiCheckSquare /> : <FiSquare />}
                              </motion.button>
                            ) : (
                              <motion.div
                                initial={false}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="delete-btn"
                                  onClick={() => handleDeleteItem(item)}
                                >
                                  <FiX />
                                </Button>
                              </motion.div>
                            )}
                            <div className="item-details">
                              <span className="item-quantity">
                                {Number.isInteger(item.qty)
                                  ? parseFloat(item.qty)
                                  : parseFloat(item.qty).toFixed(1)}{" "}
                                {item.unit_name}
                              </span>
                              <span className="item-name">{item.item_name}</span>
                            </div>
                          </div>
                          <div className="item-actions">
                            {item.original_id_string !== null && (
                              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditInstruction(item)}
                                >
                                  Edit Aisle
                                </Button>
                              </motion.div>
                            )}
                            {editMode &&
                              item.conversion_options &&
                              item.conversion_options.length > 0 && (
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${
                                      parseInt(item.store_unit_overide) !== 0 &&
                                      item.store_unit_overide
                                        ? "btn-success"
                                        : "btn-secondary"
                                    } dropdown-toggle-2`}
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    Set Unit
                                  </button>
                                  <div className="dropdown-menu">
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() =>
                                        handleSetUnitOptionDefault(
                                          item.original_id_string
                                        )
                                      }
                                    >
                                      <span>Default</span>
                                    </button>
                                    {item.conversion_options.map((option, index) => (
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        key={index}
                                        onClick={() =>
                                          handleSetUnitOption(
                                            item.original_id_string,
                                            option.id
                                          )
                                        }
                                      >
                                        <span>{option.unit_name}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </LayoutGroup>
    );
  };

  const handleUpdateAisleInformation = () => {
    console.log(selectedItemEditAisle.original_id_string);
    const original_id = selectedItemEditAisle.original_id_string;

    const ingredient_id =
      selectedItemEditAisle.original_id_string.split(",")[0];
    console.log(ingredient_id);
    console.log(selectedEditAisle);

    if (original_id.includes("user")) {
      fetch(`${process.env.REACT_APP_API_URL}/api/update_user_aisle`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: ingredient_id,
          aisle_id: selectedEditAisle.id,
          aisle_name: selectedEditAisle.aisle_name,
          original_id: original_id,
        }),
      })
        .then((res) => {
          if (res.ok) {
            console.log("success");
            fetchShoppingList();
            fetchDeletedShoppingList();
            setSelectedItemEditAisle(null);
          } else {
            throw new Error("Error moving aisle");
          }
        })
        .catch((err) => {
          console.error("Error deleting aisle:", err);
        });
    } else if (original_id.includes("non-cooking")) {
      fetch(
        `${process.env.REACT_APP_API_URL}/api/update_user_aisle_non_cooking`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: ingredient_id,
            aisle_id: selectedEditAisle.id,
            aisle_name: selectedEditAisle.aisle_name,
            original_id: original_id,
          }),
        }
      )
        .then((res) => {
          if (res.ok) {
            console.log("success");
            fetchShoppingList();
            fetchDeletedShoppingList();
            setSelectedItemEditAisle(null);
          } else {
            throw new Error("Error moving aisle");
          }
        })
        .catch((err) => {
          console.error("Error deleting aisle:", err);
        });
    } else {
      fetch(
        `${process.env.REACT_APP_API_URL}/user-ingredient-customization?ingredient_id=${ingredient_id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
        .then((res) => {
          if (res.ok) {
            return res.json();
          } else {
            throw new Error("Error querying user ingredient customization");
          }
        })
        .then((data) => {
          console.log(data);
          try {
            if (data.length === 0) {
              // No match, create new entry in user_ingredient_customizations table
              fetch(
                `${process.env.REACT_APP_API_URL}/api/insert_update_non_user_aisle`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    id: ingredient_id,
                    aisle_id: selectedEditAisle.id,
                    aisle_name: selectedEditAisle.aisle_name,
                    original_id: original_id,
                  }),
                }
              )
                .then((res) => {
                  if (res.ok) {
                    console.log("success");
                    fetchShoppingList();
                    fetchDeletedShoppingList();
                    setSelectedItemEditAisle(null);
                  } else {
                    throw new Error("Error moving aisle");
                  }
                })
                .catch((err) => {
                  console.error("Error deleting aisle:", err);
                });
            } else {
              // Match found, update existing entry in user_ingredient_customizations table

              fetch(
                `${process.env.REACT_APP_API_URL}/api/update_non_user_aisle`,
                {
                  method: "PUT",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    id: ingredient_id,
                    aisle_id: selectedEditAisle.id,
                    aisle_name: selectedEditAisle.aisle_name,
                    original_id: original_id,
                  }),
                }
              )
                .then((res) => {
                  if (res.ok) {
                    console.log("success");
                    fetchShoppingList();
                    fetchDeletedShoppingList();
                    setSelectedItemEditAisle(null);
                  } else {
                    throw new Error("Error moving aisle");
                  }
                })
                .catch((err) => {
                  console.error("Error deleting aisle:", err);
                });
            }
          } catch (err) {
            console.error("Error parsing JSON response:", err);
          }
        })
        .catch((err) => {
          console.error("Error querying user ingredient customization:", err);
        });
    }
  };

  const handleSetUnitOptionDefault = (original_id, conversionId) => {
    console.log(original_id);

    fetch(`${process.env.REACT_APP_API_URL}/api/setShoppingUnit`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        original_id: original_id,
        conversionId: 0,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Success");
        fetchIngredients();
        fetchFavIngredients();
        fetchShoppingList();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleSetUnitOption = (original_id, conversionId) => {
    console.log(original_id);
    console.log(conversionId);

    fetch(`${process.env.REACT_APP_API_URL}/api/setShoppingUnit`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        original_id: original_id,
        conversionId: conversionId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Success");
        fetchIngredients();
        fetchFavIngredients();
        fetchShoppingList();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // Fetch day filter preference
  const fetchDayFilter = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shopping-day-filter`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      if (data.filter) {
        setSelectedDays(data.filter.split(','));
      } else {
        setSelectedDays(ALL_DAYS); // Default: all selected
      }
      setDayFilterLoaded(true);
    } catch (err) {
      console.error("Error fetching day filter:", err);
      setDayFilterLoaded(true);
    }
  };

  // Handle day filter change with debounced save
  const handleDayFilterChange = (day) => {
    const newSelectedDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];

    setSelectedDays(newSelectedDays);

    // Debounce the save
    if (saveFilterTimeoutRef.current) {
      clearTimeout(saveFilterTimeoutRef.current);
    }

    saveFilterTimeoutRef.current = setTimeout(async () => {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/shopping-day-filter`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filter:
              newSelectedDays.length === ALL_DAYS.length
                ? null
                : newSelectedDays.join(","),
          }),
        });
      } catch (err) {
        console.error("Error saving day filter:", err);
      }
    }, 300);
  };

  // Select/clear all days
  const selectAllDays = () => {
    setSelectedDays(ALL_DAYS);
    // Save immediately
    fetch(`${process.env.REACT_APP_API_URL}/api/shopping-day-filter`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filter: null }),
    }).catch((err) => console.error("Error saving day filter:", err));
  };

  const clearAllDays = () => {
    setSelectedDays([]);
    // Save immediately
    fetch(`${process.env.REACT_APP_API_URL}/api/shopping-day-filter`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filter: "" }),
    }).catch((err) => console.error("Error saving day filter:", err));
  };

  // Filter items by selected days and aggregate quantities
  const getFilteredItems = (items) => {
    // First filter by selected days
    const filtered = items.filter((item) => {
      // Check if day is "unspecified" (null, undefined, empty string, or "None")
      const isUnspecified = item.day === null || item.day === undefined || item.day === "" || item.day === "None";
      if (isUnspecified) {
        return selectedDays.includes("Unspecified");
      }
      return selectedDays.includes(item.day);
    });

    // Then aggregate items with the same original_id_string (or manual_name for manual items)
    const aggregated = filtered.reduce((acc, item) => {
      // Use original_id_string as key, or manual item_name for manual items
      const key = item.original_id_string || `manual_${item.item_name}`;

      if (!acc[key]) {
        // First occurrence - clone the item and track all IDs
        acc[key] = { ...item, _allIds: [item.id] };
      } else {
        // Aggregate quantities and track all IDs
        acc[key].qty = (parseFloat(acc[key].qty) || 0) + (parseFloat(item.qty) || 0);
        acc[key]._allIds.push(item.id);
      }

      return acc;
    }, {});

    return Object.values(aggregated);
  };

  // Get total unique items count (aggregated across ALL days, ignoring filter)
  const getTotalUniqueItemsCount = (items) => {
    const uniqueKeys = new Set(
      items.map((item) => item.original_id_string || `manual_${item.item_name}`)
    );
    return uniqueKeys.size;
  };

  useEffect(() => {
    // Fetch user units when component is mounted
    setIsLoading(true);
    Promise.all([
      fetchShoppingList(),
      fetchDeletedShoppingList(),
      fetchIngredients(),
      fetchFavIngredients(),
      fetchAisles(),
      fetchUserMetricSetting(),
      fetchDayFilter()
    ]).finally(() => {
      setIsLoading(false);
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + P for print
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
      // Ctrl/Cmd + F to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        document.querySelector('.react-select__input input')?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handlePrint]);

  if (!isLoggedIn) {
    return (
      <h3>
        Please{" "}
        <a
          className="btn btn-primary"
          href={`/signin?returnUrl=${encodeURIComponent(
            window.location.pathname
          )}`}
        >
          Sign In
        </a>{" "}
        to view
      </h3>
    );
  }

  if (isLoading) {
    return <LoadingSkeleton type="list" />;
  }

  return (
    <div className={`shopping-list-container ${printMode ? 'print-mode' : ''}`} ref={componentRef}>
      <motion.div 
        className="main-container"
        initial={animations.fade.initial}
        animate={animations.fade.animate}
        transition={transitions.smooth}
      >
        <div className="row mb-2">
          <div className="col-sm-5">
            <motion.div 
              className="section-card"
              {...animations.card}
              transition={transitions.smooth}
            >
              <div className="section-header">
                <h5>
                  <FiSearch className="section-icon" />
                  Search an existing item
                  <OverlayTrigger
                    delay={{ hide: 150, show: 150 }}
                    overlay={(props) => (
                      <Tooltip {...props}>
                        This list is from your unhidden "Ingredients" and
                        "Non-Cooking Items".
                      </Tooltip>
                    )}
                    placement="bottom"
                  >
                    <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
                  </OverlayTrigger>
                </h5>
              </div>
              <div className="row align-items-end g-2">
                <div className="col-sm-3">
                  <div className="form-group">
                    <label><FiHash className="input-icon" /> Qty:</label>
                    <div className="input-with-icon">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Qty"
                        min="0"
                        value={ingredientQty}
                        onChange={(e) =>
                          handleIngredientQtyChange(parseFloat(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="form-group">
                    <label>
                      <FiShoppingCart className="input-icon" />
                      Item
                      {selectedIngredientUnit !== "" && (
                        <span className="unit-label">
                          {" · Unit: "}
                          {selectedIngredientUnit}
                        </span>
                      )}
                    </label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      key={selectedIngredient}
                      options={ingredientOptions}
                      value={selectedIngredient.id ? {
                        id: selectedIngredient.id,
                        ingredient_name: selectedIngredient.ingredient_name,
                      } : null}
                      onChange={(selectedOption) => {
                        setSelectedIngredient(selectedOption || {});
                        setSelectedIngredientUnit(selectedOption ? selectedOption.unit_id : "");
                      }}
                      getOptionLabel={(option) => option.ingredient_name}
                      getOptionValue={(option) => option.id}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Search ingredients..."
                      noOptionsMessage={() => "No ingredients found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        option: (provided) => ({
                          ...provided,
                          color: "black",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "black",
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          color: "black",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-sm-3 d-flex align-items-end">
                  <motion.div className="w-100" {...animations.buttonHover}>
                    <Button variant="primary" onClick={handleAddIngredient} className="w-100">
                      Add Item
                    </Button>
                  </motion.div>
                </div>
              </div>
              <AnimatePresence>
                {successAnimation === 'ingredient' && (
                  <motion.div
                    className="success-checkmark"
                    initial={animations.success.initial}
                    animate={animations.success.animate}
                    exit={animations.fade.exit}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <FiCheck style={{ fontSize: '3rem', color: '#28a745' }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
          <div className="col-sm-7">
            <motion.div 
              className="section-card"
              {...animations.card}
              transition={transitions.smooth}
            >
              <div className="section-header">
                <h5>
                  <FiEdit3 className="section-icon" />
                  Manual add an item
                  <OverlayTrigger
                    delay={{ hide: 150, show: 150 }}
                    overlay={(props) => (
                      <Tooltip {...props}>
                        Manual items are not saved for future use and cannot be
                        edited. They are intended for a "one-time" use.
                      </Tooltip>
                    )}
                    placement="bottom"
                  >
                    <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
                  </OverlayTrigger>
                </h5>
              </div>
              <div className="row align-items-end g-2">
                <div className="col-sm-2">
                  <div className="form-group">
                    <label><FiHash className="input-icon" /> Qty:</label>
                    <div className="input-with-icon">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Qty"
                        min="0"
                        value={itemQty}
                        onChange={(e) =>
                          handleItemQtyChange(parseFloat(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-group">
                    <label><FiShoppingCart className="input-icon" /> Item:</label>
                    <div className="input-with-icon">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter item name"
                        value={itemName}
                        onChange={handleItemNameChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-3">
                  <div className="form-group">
                    <label><FiMapPin className="input-icon" /> Aisle:</label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      key={selectedAisle}
                      value={selectedAisle.id ? {
                        aisle_name: selectedAisle.aisle_name,
                        id: selectedAisle.id,
                      } : null}
                      options={aisles}
                      onChange={(selectedOption) => {
                        setSelectedAisle(selectedOption || {});
                      }}
                      getOptionLabel={(option) => option.aisle_name}
                      getOptionValue={(option) => option.id}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Select aisle..."
                      noOptionsMessage={() => "No aisles found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        option: (provided) => ({
                          ...provided,
                          color: "black",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "black",
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          color: "black",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>

                <div className="col-sm-3 d-flex align-items-end">
                  <motion.div className="w-100" {...animations.buttonHover}>
                    <Button variant="primary" onClick={handleAddManualItem} className="w-100">
                      Add Item
                    </Button>
                  </motion.div>
                </div>
              </div>
              <AnimatePresence>
                {successAnimation === 'manual' && (
                  <motion.div
                    className="success-checkmark"
                    initial={animations.success.initial}
                    animate={animations.success.animate}
                    exit={animations.fade.exit}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <FiCheck style={{ fontSize: '3rem', color: '#28a745' }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-5">
            <motion.div 
              className="section-card"
              {...animations.card}
              transition={transitions.smooth}
            >
              <div className="section-header">
                <h5>
                  <FiStar className="section-icon" />
                  Search from favorites
                  <OverlayTrigger
                    delay={{ hide: 150, show: 150 }}
                    overlay={(props) => (
                      <Tooltip {...props}>
                        This list is from your unhidden favorited "Ingredients"
                        and "Non-Cooking Items".
                      </Tooltip>
                    )}
                    placement="bottom"
                  >
                    <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
                  </OverlayTrigger>
                </h5>
              </div>
              <div className="row align-items-end g-2">
                <div className="col-sm-3">
                  <div className="form-group">
                    <label><FiHash className="input-icon" /> Qty:</label>
                    <div className="input-with-icon">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Qty"
                        min="0"
                        value={favIngredientQty}
                        onChange={(e) =>
                          handleFavIngredientQtyChange(parseFloat(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="form-group">
                    <label>
                      <FiShoppingCart className="input-icon" />
                      Item
                      {favSelectedIngredientUnit !== "" && (
                        <span className="unit-label">
                          {" · Unit: "}
                          {favSelectedIngredientUnit}
                        </span>
                      )}
                    </label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      key={favSelectedIngredient}
                      options={favIngredientOptions}
                      value={favSelectedIngredient.id ? {
                        id: favSelectedIngredient.id,
                        ingredient_name: favSelectedIngredient.ingredient_name,
                      } : null}
                      onChange={(selectedOption) => {
                        setFavSelectedIngredient(selectedOption || {});
                        setFavSelectedIngredientUnit(selectedOption ? selectedOption.unit_id : "");
                      }}
                      getOptionLabel={(option) => option.ingredient_name}
                      getOptionValue={(option) => option.id}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Search favorites..."
                      noOptionsMessage={() => "No favorite items found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        option: (provided) => ({
                          ...provided,
                          color: "black",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "black",
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          color: "black",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-sm-3 d-flex align-items-end">
                  <motion.div className="w-100" {...animations.buttonHover}>
                    <Button variant="primary" onClick={handleFavAddIngredient} className="w-100">
                      Add Item
                    </Button>
                  </motion.div>
                </div>
              </div>
              <AnimatePresence>
                {successAnimation === 'favorite' && (
                  <motion.div
                    className="success-checkmark"
                    initial={animations.success.initial}
                    animate={animations.success.animate}
                    exit={animations.fade.exit}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <FiCheck style={{ fontSize: '3rem', color: '#28a745' }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
          <div className="col-sm-4">
            <motion.div 
              className="section-card favorites-management-section"
              {...animations.card}
              transition={transitions.smooth}
            >
              <div className="section-header">
                <h5>
                  <FiStar className="section-icon" />
                  Favorites Management
                  <OverlayTrigger
                    delay={{ hide: 150, show: 150 }}
                    overlay={(props) => (
                      <Tooltip {...props}>
                        Add and remove items from your favorites list.
                      </Tooltip>
                    )}
                    placement="bottom"
                  >
                    <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
                  </OverlayTrigger>
                </h5>
                <motion.button
                  className="btn btn-link toggle-favorites-btn"
                  onClick={handleFavEditMode}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiSettings className="me-1" />
                  {showFavoritesPanel ? "Hide" : "Show"}
                </motion.button>
              </div>
              
              {/* Show info when panel is closed */}
              {!showFavoritesPanel && (
                <div className="favorites-info">
                  <p className="text-muted small text-center mb-0">
                    Click "Show" to add or remove items from your favorites list
                  </p>
                </div>
              )}
              
              {/* Favorites Management Panel */}
              <AnimatePresence>
                {showFavoritesPanel && (
                  <motion.div 
                    className="favorites-management-panel"
                    initial={animations.collapse.initial}
                    animate={animations.collapse.animate}
                    exit={animations.collapse.exit}
                    transition={animations.collapse.transition}
                  >
                    <div className="add-favorite-section mb-3">
                      <div className="row align-items-end">
                        <div className="col-sm-8">
                          <label className="form-label">
                            Add item to favorites:
                          </label>
                          <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            key={modalSelectedIngredient}
                            options={ingredientOptions}
                            value={modalSelectedIngredient.id ? {
                              id: modalSelectedIngredient.id,
                              ingredient_name: modalSelectedIngredient.ingredient_name,
                            } : null}
                            onChange={(selectedOption) => {
                              setModalSelectedIngredient(selectedOption || {});
                            }}
                            getOptionLabel={(option) => option.ingredient_name}
                            getOptionValue={(option) => option.id}
                            isSearchable={true}
                            isClearable={true}
                            placeholder="Search items to add to favorites..."
                            noOptionsMessage={() => "No items found"}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              option: (provided) => ({
                                ...provided,
                                color: "black",
                              }),
                              singleValue: (provided) => ({
                                ...provided,
                                color: "black",
                              }),
                              control: (provided, state) => ({
                                ...provided,
                                color: "black",
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                        <div className="col-sm-4">
                          <motion.div {...animations.buttonHover}>
                            <Button 
                              variant="primary" 
                              onClick={handleAddFavoriteToList}
                              disabled={!modalSelectedIngredient.id}
                              className="w-100"
                            >
                              <FiStar className="me-1" />
                              Add to Favorites
                            </Button>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="current-favorites-section">
                      <p className="fw-bold mb-2">Current Favorites ({favIngredientOptions.length}):</p>
                      {favIngredientOptions.length > 0 ? (
                        <div className="favorites-list">
                          <AnimatePresence>
                            {favIngredientOptions.map((item) => (
                              <motion.div 
                                key={item.id} 
                                className="favorite-item"
                                initial={animations.listItem.initial}
                                animate={animations.listItem.animate}
                                exit={animations.listItem.exit}
                                transition={animations.listItem.transition}
                                layout
                              >
                                <span className="favorite-name">{item.ingredient_name}</span>
                                <motion.div {...animations.buttonHover}>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="remove-favorite-btn"
                                    onClick={() => handleRemoveFavorite(item)}
                                  >
                                    <FiTrash2 className="me-1" />
                                    Remove
                                  </Button>
                                </motion.div>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <p className="text-muted text-center">No favorites yet.</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
          <div className="col-sm-3">
            <motion.div 
              className="section-card"
              {...animations.card}
              transition={transitions.smooth}
            >
              <div className="section-header">
                <h5>Options</h5>
              </div>
              <div className="d-flex flex-column gap-1">
                <Form.Switch
                  type="switch"
                  id="custom-switch"
                  label={"Use Metric"}
                  checked={isMetric}
                  onChange={handleToggle}
                />
                <div className="d-flex align-items-center">
                  <Form.Switch
                    type="switch"
                    id="custom-switch2"
                    label={"Edit Mode"}
                    checked={editMode}
                    onChange={handleEditMode}
                  />
                  <OverlayTrigger
                    delay={{ hide: 150, show: 150 }}
                    overlay={(props) => (
                      <Tooltip {...props}>
                        Allows you to set and save the 'unit of measurement'
                        displayed for each item.
                      </Tooltip>
                    )}
                    placement="bottom"
                  >
                    <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
                  </OverlayTrigger>
                </div>
                <div className="d-flex align-items-center">
                  <Form.Switch
                    type="switch"
                    id="custom-switch3"
                    label={"Multi-select Mode"}
                    checked={multiSelectMode}
                    onChange={handleMultiSelectMode}
                  />
                  <OverlayTrigger
                    delay={{ hide: 150, show: 150 }}
                    overlay={(props) => (
                      <Tooltip {...props}>
                        Select multiple items for bulk operations.
                      </Tooltip>
                    )}
                    placement="bottom"
                  >
                    <FontAwesomeIcon className="mx-2" icon={faQuestionCircle} />
                  </OverlayTrigger>
                </div>
                <motion.div {...animations.buttonHover}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handlePrint}
                    className="w-100 mt-2"
                  >
                    <FiPrinter className="me-1" />
                    Print List
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="shopping-list-display"
        initial={animations.fade.initial}
        animate={animations.fade.animate}
        transition={transitions.smooth}
      >
        <div className="shopping-list-header">
          <h3>Shopping List</h3>
          <div className="list-header-actions">
            <div className="list-stats">
              <span className="item-count">
                {selectedDays.length === ALL_DAYS.length
                  ? `${getFilteredItems(shoppingListItems).length} items`
                  : `${getFilteredItems(shoppingListItems).length} of ${getTotalUniqueItemsCount(shoppingListItems)} items`}
              </span>
              {deletedShoppingListItems.length > 0 && (
                <motion.span 
                  className="completed-count"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 15 }}
                >
                  <FiCheck className="check-icon" />
                  {deletedShoppingListItems.length} completed
                </motion.span>
              )}
            </div>
            {shoppingListItems.length > 0 && (
              <motion.div {...animations.buttonHover}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleClearAllItems}
                  className="clear-all-btn"
                >
                  <FiTrash2 className="me-1" />
                  Clear All
                </Button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Day Filter Section */}
        <div className="day-filter-section">
          <div className="day-filter-header">
            <span className="day-filter-label">Filter by Day:</span>
            <div className="day-filter-actions">
              <button
                className="btn btn-link btn-sm"
                onClick={selectAllDays}
                disabled={selectedDays.length === ALL_DAYS.length}
              >
                Select All
              </button>
              <button
                className="btn btn-link btn-sm"
                onClick={clearAllDays}
                disabled={selectedDays.length === 0}
              >
                Clear All
              </button>
            </div>
          </div>
          <div className="day-filter-checkboxes">
            {ALL_DAYS.map((day) => (
              <label key={day} className="day-filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedDays.includes(day)}
                  onChange={() => handleDayFilterChange(day)}
                />
                <span className="day-label">
                  {day === "Unspecified" ? "Unspecified" : day.substring(0, 3)}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Bulk Operations Toolbar */}
        <AnimatePresence>
          {multiSelectMode && (
            <motion.div
              className="bulk-operations-toolbar"
              initial={animations.fade.initial}
              animate={animations.fade.animate}
              exit={animations.fade.exit}
              transition={animations.fade.transition}
            >
              <div className="selection-info">
                <span>{selectedItems.size} of {getFilteredItems(shoppingListItems).length} selected</span>
              </div>
              <div className="bulk-actions">
                <motion.div {...animations.buttonHover}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={selectAllItems}
                    disabled={selectedItems.size === getFilteredItems(shoppingListItems).length}
                  >
                    Select All
                  </Button>
                </motion.div>
                <motion.div {...animations.buttonHover}>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelection}
                    disabled={selectedItems.size === 0}
                  >
                    Clear Selection
                  </Button>
                </motion.div>
                <motion.div {...animations.buttonHover}>
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={handleBulkDelete}
                    disabled={selectedItems.size === 0}
                  >
                    <FiTrash2 className="me-1" />
                    Delete Selected ({selectedItems.size})
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <p className="text-muted small mb-3">
          Click "Edit Aisle" for non-manually added items to update and save the
          aisle information.
        </p>
        <div className="shopping-list-items">
          {shoppingListItems.length > 0 ? (
            renderShoppingListByAisle()
          ) : (
            <motion.div 
              className="empty-state"
              initial={animations.fade.initial}
              animate={animations.fade.animate}
              transition={transitions.smooth}
            >
              <FiShoppingCart className="empty-icon" />
              <p>Your shopping list is empty</p>
              <p className="text-muted">Add items using the forms above to get started</p>
            </motion.div>
          )}
        </div>
      </motion.div>
      
      <div className="crossed-off-section-simple">
        <div className="crossed-off-header">
          <h3>Crossed Off Items</h3>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleClearDeletedItems}
          >
            Clear All
          </Button>
        </div>
        <div className="crossed-off-items">
          {deletedShoppingListItems.length > 0 ? (
            <div className="simple-completed-list">
              {deletedShoppingListItems.map((item) => (
                <div key={item.id} className="simple-completed-item">
                  <div className="item-info">
                    <FiCheck className="check-icon" />
                    <span className="item-text">
                      {Number.isInteger(item.qty)
                        ? parseFloat(item.qty)
                        : parseFloat(item.qty).toFixed(1)}{" "}
                      {item.unit_name} {item.item_name}
                      {item.aisle_name && (
                        <span className="aisle-info"> - {item.aisle_name}</span>
                      )}
                    </span>
                  </div>
                  {item.original_id_string !== null && (
                    <Button
                      variant="link"
                      size="sm"
                      className="edit-aisle-link"
                      onClick={() => handleEditInstruction(item)}
                    >
                      Edit Aisle
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted text-center">No completed items</p>
          )}
        </div>
      </div>
      
      <Modal
        show={selectedItemEditAisle !== null}
        onHide={() => setSelectedItemEditAisle(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Aisle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Edit Aisle for <b>{selectedItemEditItemName}</b>
          <Select
            key={selectedEditAisle}
            value={{
              aisle_name: selectedEditAisle.aisle_name,
              id: selectedEditAisle.aisle_id,
            }}
            options={aisles}
            onChange={(selectedOption) => {
              setSelectedEditAisle(selectedOption);
            }}
            getOptionLabel={(option) => option.aisle_name}
            getOptionValue={(option) => option.id}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              option: (provided) => ({
                ...provided,
                color: "black",
              }),
              singleValue: (provided) => ({
                ...provided,
                color: "black",
              }),
              control: (provided, state) => ({
                ...provided,
                color: "black",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setSelectedItemEditAisle(null)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => handleUpdateAisleInformation()}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ShoppingList;